import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { parseMoneyValue } from '@/lib/currency';
import {
  buildInsightWindow,
  computeInsights,
  type InsightJob,
  type InsightRange,
  type InsightWindow,
  type Insights,
} from '@/lib/dashboard-insights';

const PAGE = 1000;

/** PostgREST caps responses at 1000 rows, so page through the jobs table. */
async function fetchJobs(orgId: string, from: string, to: string): Promise<InsightJob[]> {
  const rows: InsightJob[] = [];
  for (let offset = 0; offset < 50_000; offset += PAGE) {
    const { data, error } = await supabase
      .from('jobs')
      .select('id, scheduled_date, status, service_id, team_member_id, customer_id, client_email, client_name')
      .eq('organization_id', orgId)
      .gte('scheduled_date', from)
      .lte('scheduled_date', to)
      .order('scheduled_date', { ascending: true })
      .order('id', { ascending: true })
      .range(offset, offset + PAGE - 1);
    if (error) throw error;
    rows.push(...((data ?? []) as InsightJob[]));
    if (!data || data.length < PAGE) break;
  }
  return rows;
}

export interface DashboardInsightsResult {
  window: InsightWindow;
  insights: Insights;
}

/**
 * Reporting data for the Dashboard "Insights" tab: the chosen period plus the
 * period before it, aggregated client-side by computeInsights().
 */
export function useDashboardInsights(orgId: string | null | undefined, range: InsightRange, enabled = true) {
  return useQuery({
    queryKey: ['dashboard-insights', orgId, range],
    enabled: Boolean(orgId) && enabled,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    queryFn: async (): Promise<DashboardInsightsResult> => {
      const window = buildInsightWindow(range);
      const since = `${window.prevStart}T00:00:00`;

      const [jobs, services, members, enquiries, invoices, customers] = await Promise.all([
        fetchJobs(orgId!, window.prevStart, window.end),
        supabase.from('services').select('id, name, price').eq('organization_id', orgId!),
        supabase.from('team_members').select('id, name').eq('organization_id', orgId!),
        supabase
          .from('enquiries')
          .select('id, status, created_at, service_id, lead_source')
          .eq('organization_id', orgId!)
          .gte('created_at', since)
          .limit(5000),
        supabase
          .from('invoices')
          .select('id, status, total_amount, amount_paid, due_date')
          .eq('organization_id', orgId!)
          .in('status', ['sent', 'partially_paid', 'overdue'])
          .limit(5000),
        supabase
          .from('customers')
          .select('created_at')
          .eq('organization_id', orgId!)
          .gte('created_at', since)
          .limit(5000),
      ]);

      const insights = computeInsights(
        {
          jobs,
          services: (services.data ?? []).map((s) => ({ id: s.id, name: s.name, price: parseMoneyValue(s.price) ?? 0 })),
          members: members.data ?? [],
          enquiries: enquiries.data ?? [],
          invoices: invoices.data ?? [],
          customers: customers.data ?? [],
        },
        window,
      );
      return { window, insights };
    },
  });
}
